import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useCanaryVisibilityScoreByProvider } from '@/api/aiVisibility';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import { Panel } from '@/components/panels';
import { Skeleton } from '@/components/Skeleton';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';

const PROVIDER_COLORS: Record<string, string> = {
  chatgpt: 'bg-llm-chatgpt',
  claude: 'bg-llm-claude',
  gemini: 'bg-llm-gemini',
  perplexity: 'bg-llm-perplexity',
  sonar: 'bg-llm-perplexity',
};

const AIVisibilityProviderChart = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByProvider();
  const perplexityLabel = t`Perplexity`;
  const providerLabels: Record<string, string> = {
    chatgpt: t`ChatGPT`,
    claude: t`Claude`,
    gemini: t`Gemini`,
    perplexity: perplexityLabel,
    sonar: perplexityLabel,
  };

  return (
    <Panel
      title={<Trans>Score by platform</Trans>}
      subtitle={<Trans>Average visibility score by AI platform for the latest week.</Trans>}
      className="min-h-64"
    >
      {isLoading ? (
        <div className="flex flex-col gap-5 py-2" aria-busy="true">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-5 w-full" />
          ))}
        </div>
      ) : error ? (
        <WidgetError message={<Trans>Unable to load platform scores.</Trans>} />
      ) : !scores?.length ? (
        <Empty>
          <Trans>No platform visibility scores available.</Trans>
        </Empty>
      ) : (
        <div className="flex flex-1 flex-col justify-center gap-4 py-2">
          {scores.map(({ provider, averageScore }) => {
            const providerKey = provider.toLowerCase();
            const label = providerLabels[providerKey] ?? provider;
            const color = PROVIDER_COLORS[providerKey] ?? 'bg-gray-600';

            return (
              <div key={provider} className="flex items-center gap-3 text-sm">
                <span className="flex w-24 shrink-0 items-center gap-2 font-medium text-gray-900">
                  <span className={cn('size-2 shrink-0 rounded-full', color)} aria-hidden="true" />
                  {label}
                </span>
                <div className="h-1.5 min-w-0 flex-1 rounded-full bg-llm-track">
                  {averageScore != null && (
                    <div
                      role="progressbar"
                      aria-label={label}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={averageScore}
                      className={cn('h-full rounded-full', color)}
                      style={{ width: `${Math.max(0, Math.min(averageScore, 100))}%` }}
                    />
                  )}
                </div>
                <span className="w-9 shrink-0 text-right font-semibold text-gray-900">
                  {averageScore == null ? '—' : numberFormat(averageScore, { precision: 0 })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

export default AIVisibilityProviderChart;
